import { validateDraftUpdate } from './catalogSave';
import type { Catalog, MenuProduct, Product } from './domain';

export type Temperature = 'hot' | 'iced';
export const temperatures: Temperature[] = ['hot', 'iced'];
export interface DrinkGroup {
  id: string;
  name: string;
  description: string;
  category: string;
  imageUrl?: string;
  variants: Partial<Record<Temperature, string>>;
}
export interface MenuDrink extends Pick<DrinkGroup, 'id' | 'name' | 'description' | 'category' | 'imageUrl'> {
  variants: MenuProduct[];
  available: boolean;
}
export function drinkForProduct(catalog: Catalog, id: string) {
  const group = catalog.drinkGroups?.find(g => temperatures.some(t => g.variants[t] === id));
  const temperature = group && temperatures.find(t => group.variants[t] === id);
  return group && temperature ? { group, temperature } : undefined;
}
export function drinkProductName(catalog: Catalog, product: Product) {
  const drink = drinkForProduct(catalog, product.id);
  return drink ? `${drink.group.name} — ${drink.temperature === 'hot' ? 'Hot' : 'Iced'}` : product.name;
}
// Older published/browse snapshots lack grouping metadata. Recognize only the
// known starter IDs and explicit name suffixes; keep their orderable IDs intact.
export function withLegacyDrinkGroups<T extends MenuProduct>(products: T[]): T[] {
  const metadata = new Map<string, { drink: Omit<DrinkGroup, 'variants'>; temperature: Temperature }>();
  for (const id of starterIds) {
    const members = temperatures.flatMap(temperature => {
      const product = products.find(p => p.id === `${id}_${temperature}`);
      return product ? [{ product, temperature }] : [];
    });
    // Explicit administrator groupings always win, including partial pairings.
    if (!members.length || members.some(({ product }) => product.drink || product.temperature)) continue;
    const named = members.map(({ product, temperature }) => {
      const suffix = temperature === 'hot' ? /\s*[—–-]\s*Hot$/i : /\s*[—–-]\s*Iced$/i;
      return { product, temperature, name: suffix.test(product.name) ? product.name.replace(suffix, '').trim() : '' };
    });
    const first = named[0];
    if (!first.name || named.some(m => m.name !== first.name || m.product.category !== first.product.category)) continue;
    const imageUrl = named.find(m => m.product.imageUrl)?.product.imageUrl;
    const drink = { id: `legacy_${id}`, name: first.name, description: first.product.description, category: first.product.category, ...(imageUrl ? { imageUrl } : {}) };
    for (const member of named) metadata.set(member.product.id, { drink, temperature: member.temperature });
  }
  return products.map(product => metadata.has(product.id) ? { ...product, ...metadata.get(product.id)! } : product);
}

// Group only explicit safe menu metadata, never infer temperature from ice choices.
export function groupMenuProducts(products: MenuProduct[]): MenuDrink[] {
  const cards = new Map<string, MenuDrink>();
  for (const product of products) {
    const group = product.drink;
    const key = group ? `group:${group.id}` : `product:${product.id}`;
    let card = cards.get(key);
    if (!card) {
      card = { id: key, name: group?.name ?? product.name, description: group?.description ?? product.description, category: group?.category ?? product.category, imageUrl: group ? group.imageUrl : product.imageUrl, variants: [], available: false };
      cards.set(key, card);
    }
    card.variants.push(product);
    card.available ||= product.available;
  }
  for (const card of cards.values()) card.variants.sort((a, b) => temperatures.indexOf(a.temperature!) - temperatures.indexOf(b.temperature!));
  return [...cards.values()];
}
export function validateDrinkGroups(catalog: Catalog): string[] {
  const errors: string[] = [];
  if (catalog.drinkGroups === undefined) return errors;
  if (!Array.isArray(catalog.drinkGroups)) return ['Invalid drink groups.'];
  const ids = new Set<string>();
  const used = new Set<string>();
  for (const group of catalog.drinkGroups) {
    if (!group || typeof group.id !== 'string' || !/^[a-zA-Z0-9_-]{1,80}$/.test(group.id) || ids.has(group.id)) { errors.push('Drink group IDs must be unique.'); continue; }
    ids.add(group.id);
    if (typeof group.name !== 'string' || !group.name.trim() || typeof group.description !== 'string' || typeof group.category !== 'string' || !group.category.trim() || (group.imageUrl !== undefined && typeof group.imageUrl !== 'string')) errors.push('Enter a drink name and category.');
    if (!group.variants || typeof group.variants !== 'object' || Array.isArray(group.variants) || !Object.values(group.variants).some(Boolean) || Object.keys(group.variants).some(t => !temperatures.includes(t as Temperature))) { errors.push('Choose Hot or Iced recipes.'); continue; }
    for (const temperature of temperatures) {
      const id = group.variants[temperature];
      if (id === undefined) continue;
      const product = catalog.products.find(p => p.id === id);
      if (!product || used.has(id)) errors.push('Each recipe must belong to only one drink and temperature.');
      used.add(id);
      if (product && temperature === 'hot' && (product.iceLevels.some(ice => ice !== 'none') || product.defaults.ice !== 'none')) errors.push('Hot recipes must use no ice.');
    }
  }
  return errors;
}
export interface SaveDrinkGroups {
  groups: DrinkGroup[];
  products: Product[];
  expectedGroups: (DrinkGroup | null)[];
  expectedProducts: (Product | null)[];
}
// Merge only the reviewed records. Unrelated concurrent catalog edits are retained.
export function saveDrinkGroups(catalog: Catalog, input: SaveDrinkGroups): Catalog {
  if (!Array.isArray(input.groups) || !input.groups.length || !Array.isArray(input.products) || input.groups.length !== input.expectedGroups.length || input.products.length !== input.expectedProducts.length) throw new Error('Invalid drink update.');
  if (new Set(input.groups.map(g => g.id)).size !== input.groups.length || new Set(input.products.map(p => p.id)).size !== input.products.length) throw new Error('Duplicate drink update.');
  const matches = (current: unknown, expected: unknown) => JSON.stringify(current ?? null) === JSON.stringify(expected);
  input.groups.forEach((g, i) => { if (!matches(catalog.drinkGroups?.find(x => x.id === g.id), input.expectedGroups[i])) throw new Error('This drink changed. Review the newer saved values in this editor before trying again.'); });
  input.products.forEach((p, i) => { if (!matches(catalog.products.find(x => x.id === p.id), input.expectedProducts[i])) throw new Error('This recipe changed. Review the newer saved values in this editor before trying again.'); });
  const referenced = new Set(input.groups.flatMap(g => Object.values(g.variants)));
  if (input.products.some(p => !referenced.has(p.id))) throw new Error('Recipe does not belong to this drink update.');
  const merge = <T extends { id: string }>(current: T[], updates: T[]) => [...current.map(x => updates.find(u => u.id === x.id) ?? x), ...updates.filter(u => !current.some(x => x.id === u.id))];
  const next = { ...catalog, revision: catalog.revision + 1, drinkGroups: merge(catalog.drinkGroups ?? [], input.groups), products: merge(catalog.products, input.products) };
  const errors = validateDraftUpdate(next, catalog, [...input.groups.map(g => ({collection:'drinkGroups' as const,id:g.id})), ...input.products.map(p => ({collection:'products' as const,id:p.id}))]);
  if (errors.length) throw new Error(errors.join('\n'));
  return next;
}
const starterIds = ['americano', 'latte', 'aren', 'switch', 'vietnam_black', 'vietnam_milk'];
export function starterDrinkGroups(catalog: Catalog): DrinkGroup[] {
  return starterIds.flatMap(id => {
    const hot = catalog.products.find(p => p.id === `${id}_hot` && !p.archived);
    const iced = catalog.products.find(p => p.id === `${id}_iced` && !p.archived);
    if (!hot || !iced || catalog.drinkGroups?.some(g => g.id === id) || [hot, iced].some(p => drinkForProduct(catalog, p.id))) return [];
    return [{ id, name: hot.name.replace(/\s*[—–-]\s*Hot$/i, ''), description: hot.description || iced.description, category: hot.category || iced.category, ...(hot.imageUrl || iced.imageUrl ? { imageUrl: hot.imageUrl || iced.imageUrl } : {}), variants: { hot: hot.id, iced: iced.id } }];
  });
}
